import React, { useState } from "react";
import { loadStripe } from "@stripe/stripe-js";
import { Elements } from "@stripe/react-stripe-js";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faDollarSign } from "@fortawesome/free-solid-svg-icons";
import { IconDefinition } from "@fortawesome/fontawesome-svg-core";
import { generateDonation } from "../api/donate";
import DonationForm from "./DonationForm";
import SuccessPopup from "./FosterSuccess/FosterSuccess";

const stripePromise = loadStripe(
  process.env.REACT_APP_STRIPE_PUBLIC_KEY as string,
);

export interface DonationTier {
  value: number;
  label?: string;
}

interface DonationWidgetProps {
  /** Donation campaign sent to the backend, e.g. "pups_fund" | "pup_profile_operations" */
  donationType: string;
  /** Short fund name used in the payment confirmation heading */
  fundName: string;
  badge: string;
  heading: string;
  description: React.ReactNode;
  tiers: DonationTier[];
  submitLabel?: string;
  submitIcon?: IconDefinition;
  id?: string;
}

const DonationWidget: React.FC<DonationWidgetProps> = ({
  donationType,
  fundName,
  badge,
  heading,
  description,
  tiers,
  submitLabel = "Continue",
  submitIcon,
  id,
}) => {
  const [email, setEmail] = useState<string>("");
  const [amount, setAmount] = useState<number>(0);
  const [customAmount, setCustomAmount] = useState<string>("");
  const [isRecurring, setIsRecurring] = useState<boolean>(false);
  const [clientSecret, setClientSecret] = useState<string>("");
  const [donationId, setDonationId] = useState<number | null>(null);
  const [isProcessing, setIsProcessing] = useState<boolean>(false);
  const [showDonationBox, setShowDonationBox] = useState<boolean>(true);
  const [showPaymentBox, setShowPaymentBox] = useState<boolean>(false);
  const [showThankYouMessage, setShowThankYouMessage] =
    useState<boolean>(false);
  const [, setPaymentStatus] = useState<string>("");
  const [errorMessage, setErrorMessage] = useState<string>("");

  const handleDonateClick = async (donationAmount: number) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!email || !emailRegex.test(email)) {
      setErrorMessage("Please enter a valid email.");
      return;
    }
    if (!donationAmount || isNaN(donationAmount) || donationAmount <= 0) {
      setErrorMessage("Please enter a valid amount.");
      return;
    }

    setErrorMessage("");
    setAmount(donationAmount);
    setIsProcessing(true);
    try {
      const { donation_id, client_secret } = await generateDonation({
        amount: donationAmount,
        user_email: email,
        type: donationType,
        is_recurring: isRecurring,
      });

      setClientSecret(client_secret);
      setDonationId(donation_id);
      setShowDonationBox(false);
      setShowPaymentBox(true);
    } catch (error: any) {
      console.error("Error generating donation:", error);
      const message =
        typeof error.response?.data === "string"
          ? error.response.data
          : "Error generating donation. Please try again.";
      setErrorMessage(message);
      setIsProcessing(false);
    }
  };

  const handleContinue = () => {
    const finalAmount = customAmount !== "" ? Number(customAmount) : amount;
    handleDonateClick(finalAmount);
  };

  return (
    <section
      id={id}
      className="flex w-full flex-col items-center bg-blue-pale px-6 py-20 lg:px-16"
    >
      {showDonationBox && (
        <div className="flex w-full max-w-[1100px] flex-col items-stretch gap-10 lg:flex-row">
          {/* Form card */}
          <div className="flex w-full flex-col items-center gap-6 rounded-xl bg-white px-6 py-8 lg:flex-1 lg:px-16">
            <p className="text-2xl font-semibold text-gray-dark">
              Select an amount
            </p>

            {/* Frequency toggle */}
            <div className="flex gap-2 rounded-full bg-gray-light p-1">
              <button
                type="button"
                onClick={() => setIsRecurring(false)}
                className={`rounded-full px-6 py-2 text-base transition ${
                  !isRecurring ? "bg-blue text-white" : "text-black"
                }`}
              >
                One-time
              </button>
              <button
                type="button"
                onClick={() => setIsRecurring(true)}
                className={`rounded-full px-6 py-2 text-base transition ${
                  isRecurring ? "bg-blue text-white" : "text-black"
                }`}
              >
                Monthly
              </button>
            </div>

            {/* Amount tiers + custom */}
            <div className="flex w-full flex-col gap-6">
              <div className="grid grid-cols-2 gap-4">
                {tiers.map((tier) => {
                  const selected = customAmount === "" && amount === tier.value;
                  return (
                    <button
                      key={tier.value}
                      type="button"
                      onClick={() => {
                        setAmount(tier.value);
                        setCustomAmount("");
                      }}
                      className={`flex flex-col items-center justify-center gap-3 rounded-lg bg-blue-strong p-4 text-center transition ${
                        selected ? "ring-2 ring-orange ring-offset-2" : ""
                      }`}
                    >
                      <span className="text-xl font-semibold text-white">
                        ${tier.value}
                      </span>
                      {tier.label && (
                        <span className="text-sm font-normal text-blue-pale">
                          {tier.label}
                        </span>
                      )}
                    </button>
                  );
                })}
              </div>

              <div className="flex items-center gap-3 rounded-lg border-2 border-gray-light bg-white px-3 py-2">
                <FontAwesomeIcon
                  icon={faDollarSign}
                  className="text-gray-dark"
                />
                <input
                  type="number"
                  min="1"
                  value={customAmount}
                  onChange={(e) => {
                    setCustomAmount(e.target.value);
                    setAmount(0);
                  }}
                  placeholder="Enter a custom amount"
                  className="w-full bg-transparent text-base text-gray-dark outline-none placeholder:text-gray-dark"
                />
              </div>
            </div>

            {/* Email */}
            <div className="flex w-full flex-col gap-2">
              <label className="text-sm font-medium uppercase tracking-[0.42px] text-gray-dark">
                Email
              </label>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="Enter email"
                className="w-full rounded-lg border-2 border-gray-light bg-white px-3 py-2 text-base text-gray-dark outline-none placeholder:text-gray-dark"
              />
            </div>

            {errorMessage && (
              <p className="text-center text-red-500">{errorMessage}</p>
            )}

            <button
              type="button"
              onClick={handleContinue}
              disabled={isProcessing}
              className="flex h-12 min-w-[152px] items-center justify-center gap-2 rounded-md bg-orange px-6 text-base font-bold text-white transition hover:bg-orange-dark disabled:opacity-60"
            >
              {submitIcon && !isProcessing && (
                <FontAwesomeIcon icon={submitIcon} className="text-lg" />
              )}
              {isProcessing ? "Processing..." : submitLabel}
            </button>
          </div>

          {/* Campaign text */}
          <div className="flex w-full flex-col gap-6 lg:flex-1 lg:px-16 lg:py-6">
            <div className="flex flex-col gap-3">
              <span className="w-fit rounded bg-white px-2 py-1 text-sm font-medium uppercase tracking-[0.42px] text-blue-strong">
                {badge}
              </span>
              <h2 className="text-3xl font-bold text-blue-strong">{heading}</h2>
            </div>
            <p className="text-base font-normal text-black">{description}</p>
          </div>
        </div>
      )}

      {showPaymentBox && clientSecret && (
        <div className="flex w-full max-w-[600px] flex-col items-center gap-6 rounded-xl bg-white px-6 py-8">
          <h2 className="text-center text-2xl font-bold text-blue-strong">
            Complete your {isRecurring ? "monthly" : "one-time"} donation of $
            {amount}.00 to {fundName}
          </h2>
          <Elements stripe={stripePromise} options={{ clientSecret }}>
            <DonationForm
              clientSecret={clientSecret}
              donationId={donationId}
              setShowPaymentBox={setShowPaymentBox}
              setShowThankYouMessage={setShowThankYouMessage}
              setPaymentStatus={setPaymentStatus}
            />
          </Elements>
        </div>
      )}

      {showThankYouMessage && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <SuccessPopup
            message="Thank you for your donation to Pup Profile! A confirmation will be sent to the email provided."
            onClose={() => {
              setShowThankYouMessage(false);
              window.location.href = "/";
            }}
          />
        </div>
      )}
    </section>
  );
};

export default DonationWidget;
